import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Drawer,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputAdornment,
  Avatar,
  Divider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BuildIcon from '@mui/icons-material/Build';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import { useNavigate } from 'react-router-dom';
import { format, addDays, addMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, parseISO, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { reservationService, vehicleTypeService } from '../../services/api';
import type { Reservation, Vehicle, VehicleType } from '../../types';

const STATUS_LABELS: Record<string, string> = { pending: 'En attente', confirmed: 'Confirmée', completed: 'Terminée', cancelled: 'Annulée' };
const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = { pending: 'warning', confirmed: 'info', completed: 'success', cancelled: 'error' };
const STATUS_BG: Record<string, string> = { pending: '#fff3e0', confirmed: '#e3f2fd', completed: '#c8e6c9', cancelled: '#ffcdd2' };
const STATUS_TEXT: Record<string, string> = { confirmed: '#0d47a1', pending: '#e65100', completed: '#1b5e20', cancelled: '#b71c1c' };
const HERO_IMAGE = 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1920&q=80';

type ViewMode = 'week' | 'month';

const Planning: React.FC = () => {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);

  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [rangeStart, setRangeStart] = useState(() => startOfWeek(today, { weekStartsOn: 1 }));
  const [rangeEnd, setRangeEnd] = useState(() => endOfWeek(today, { weekStartsOn: 1 }));
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<number | ''>('');
  const [transmissionFilter, setTransmissionFilter] = useState<'' | 'Automatique' | 'Manuelle'>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [detailReservation, setDetailReservation] = useState<Reservation | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [maintenanceAnchor, setMaintenanceAnchor] = useState<HTMLElement | null>(null);
  const [maintenanceVehicle, setMaintenanceVehicle] = useState<any>(null);

  const days = useMemo(() => eachDayOfInterval({ start: rangeStart, end: rangeEnd }), [rangeStart, rangeEnd]);
  const dayWidth = viewMode === 'month' ? 40 : 110;

  useEffect(() => {
    vehicleTypeService.getAll().then((res) => setVehicleTypes(res.data.data || [])).catch(() => setVehicleTypes([]));
  }, []);

  const fetchPlanning = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { start: format(rangeStart, 'yyyy-MM-dd'), end: format(rangeEnd, 'yyyy-MM-dd') };
      if (vehicleTypeFilter !== '') params.vehicle_type_id = Number(vehicleTypeFilter);
      if (transmissionFilter) params.transmission = transmissionFilter;
      if (statusFilter) params.status = statusFilter;
      if (searchText.trim()) params.search = searchText.trim();
      const res = await reservationService.getPlanning(params);
      const { vehicles: v, reservations: r } = res.data.data;
      setVehicles(v || []);
      setReservations(r || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement du planning.');
      setVehicles([]);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd, vehicleTypeFilter, transmissionFilter, statusFilter, searchText]);

  useEffect(() => { fetchPlanning(); }, [fetchPlanning]);

  const switchView = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'week') {
      setRangeStart(startOfWeek(today, { weekStartsOn: 1 }));
      setRangeEnd(endOfWeek(today, { weekStartsOn: 1 }));
    } else {
      setRangeStart(startOfMonth(today));
      setRangeEnd(endOfMonth(today));
    }
  };

  const goPrev = () => {
    if (viewMode === 'month') {
      const prev = addMonths(rangeStart, -1);
      setRangeStart(startOfMonth(prev));
      setRangeEnd(endOfMonth(prev));
    } else {
      const diff = Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      setRangeStart((d) => addDays(d, -diff));
      setRangeEnd((d) => addDays(d, -diff));
    }
  };

  const goNext = () => {
    if (viewMode === 'month') {
      const next = addMonths(rangeStart, 1);
      setRangeStart(startOfMonth(next));
      setRangeEnd(endOfMonth(next));
    } else {
      const diff = Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      setRangeStart((d) => addDays(d, diff));
      setRangeEnd((d) => addDays(d, diff));
    }
  };

  const goToday = () => {
    if (viewMode === 'month') {
      setRangeStart(startOfMonth(today));
      setRangeEnd(endOfMonth(today));
    } else {
      setRangeStart(startOfWeek(today, { weekStartsOn: 1 }));
      setRangeEnd(endOfWeek(today, { weekStartsOn: 1 }));
    }
  };

  const resetFilters = () => { setVehicleTypeFilter(''); setTransmissionFilter(''); setStatusFilter(''); setSearchText(''); };
  const hasActiveFilters = vehicleTypeFilter !== '' || transmissionFilter !== '' || statusFilter !== '' || searchText.trim() !== '';

  const reservationsByVehicle = useMemo(() => reservations.reduce<Record<number, Reservation[]>>((acc, r) => { const vid = r.vehicle_id; if (!acc[vid]) acc[vid] = []; acc[vid].push(r); return acc; }, {}), [reservations]);

  const reservationOverlapsDay = (res: Reservation, day: Date) => {
    const start = res.pickup_datetime ? parseISO(res.pickup_datetime) : null;
    const end = res.return_datetime ? parseISO(res.return_datetime) : null;
    if (!start || !end) return false;
    const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 999);
    return start <= dayEnd && end >= dayStart;
  };

  const openDetail = (res: Reservation) => { setDetailReservation(res); setDetailDrawerOpen(true); };
  const isToday = (day: Date) => format(day, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
  const isWeekend = (day: Date) => day.getDay() === 0 || day.getDay() === 6;

  const handleMaintenanceClick = (event: React.MouseEvent<HTMLElement>, vehicle: any) => {
    event.stopPropagation();
    setMaintenanceAnchor(event.currentTarget);
    setMaintenanceVehicle(vehicle);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      {/* Hero Header with search */}
      <Box sx={{ position: 'relative', height: { xs: 200, md: 260 }, backgroundImage: `linear-gradient(135deg, rgba(44,62,80,0.92) 0%, rgba(52,73,94,0.88) 100%), url(${HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>Planning des véhicules</Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mb: 3 }}>Vue d'ensemble de la disponibilité et des réservations</Typography>
        <Paper elevation={8} sx={{ p: 2, borderRadius: 3, maxWidth: 720, width: '100%', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
          <TextField size="small" type="date" label="Du" value={format(rangeStart, 'yyyy-MM-dd')} onChange={(e) => { if (e.target.value) { const d = new Date(e.target.value); setRangeStart(startOfDay(d)); if (d > rangeEnd) setRangeEnd(endOfDay(d)); } }} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 18 }} /></InputAdornment> }} sx={{ flex: 1, minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafbfc' } }} />
          <TextField size="small" type="date" label="Au" value={format(rangeEnd, 'yyyy-MM-dd')} onChange={(e) => { if (e.target.value) { const d = new Date(e.target.value); setRangeEnd(endOfDay(d)); if (d < rangeStart) setRangeStart(startOfDay(d)); } }} InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 18 }} /></InputAdornment> }} sx={{ flex: 1, minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafbfc' } }} />
          <TextField size="small" placeholder="Rechercher..." value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }} sx={{ flex: 1, minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafbfc' } }} />
          <Button variant="contained" onClick={() => fetchPlanning()} sx={{ borderRadius: 2, px: 3, py: 1, textTransform: 'none', fontWeight: 600, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}>Rechercher</Button>
        </Paper>
      </Box>

      {/* Filter Bar in header zone */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e8ecf1', py: 2, px: 2 }}>
        <Box sx={{ mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <FilterListIcon fontSize="small" sx={{ color: '#2c3e50' }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#2c3e50' }}>Filtres</Typography>
            {hasActiveFilters && <Chip label="Réinitialiser" size="small" onDelete={resetFilters} onClick={resetFilters} sx={{ ml: 1, height: 24, fontSize: '0.75rem' }} />}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>Type de véhicule</Typography>
              <RadioGroup row value={vehicleTypeFilter === '' ? 'all' : String(vehicleTypeFilter)} onChange={(_, v) => setVehicleTypeFilter(v === 'all' ? '' : Number(v))}>
                <FormControlLabel value="all" control={<Radio size="small" />} label="Tous" />
                {vehicleTypes.map((vt) => <FormControlLabel key={vt.id} value={String(vt.id)} control={<Radio size="small" />} label={vt.name || `Type ${vt.id}`} />)}
              </RadioGroup>
            </Box>
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>Transmission</Typography>
              <RadioGroup row value={transmissionFilter || 'all'} onChange={(_, v) => setTransmissionFilter(v === 'all' ? '' : v as any)}>
                <FormControlLabel value="all" control={<Radio size="small" />} label="Toutes" />
                <FormControlLabel value="Automatique" control={<Radio size="small" />} label="Automatique" />
                <FormControlLabel value="Manuelle" control={<Radio size="small" />} label="Manuelle" />
              </RadioGroup>
            </Box>
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>Statut réservation</Typography>
              <RadioGroup row value={statusFilter || 'all'} onChange={(_, v) => setStatusFilter(v === 'all' ? '' : v)}>
                <FormControlLabel value="all" control={<Radio size="small" />} label="Tous" />
                <FormControlLabel value="pending" control={<Radio size="small" />} label="En attente" />
                <FormControlLabel value="confirmed" control={<Radio size="small" />} label="Confirmée" />
                <FormControlLabel value="completed" control={<Radio size="small" />} label="Terminée" />
                <FormControlLabel value="cancelled" control={<Radio size="small" />} label="Annulée" />
              </RadioGroup>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Calendar Section */}
      <Box sx={{ px: 2, py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Navigation + View Toggle */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && switchView(v)} size="small" sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <ToggleButton value="week" sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}><ViewWeekIcon sx={{ mr: 0.5, fontSize: 18 }} />Semaine</ToggleButton>
            <ToggleButton value="month" sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}><CalendarViewMonthIcon sx={{ mr: 0.5, fontSize: 18 }} />Mois</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            <IconButton onClick={goPrev} size="small" sx={{ borderRadius: 0, px: 1.5 }}><ChevronLeftIcon /></IconButton>
            <Divider orientation="vertical" flexItem />
            <Button startIcon={<TodayIcon />} onClick={goToday} size="small" sx={{ borderRadius: 0, textTransform: 'none', px: 2, fontWeight: 600, color: '#2c3e50' }}>Aujourd'hui</Button>
            <Divider orientation="vertical" flexItem />
            <IconButton onClick={goNext} size="small" sx={{ borderRadius: 0, px: 1.5 }}><ChevronRightIcon /></IconButton>
          </Box>
          <Typography variant="body2" fontWeight={600} color="text.secondary">{format(rangeStart, 'd MMMM yyyy', { locale: fr })} — {format(rangeEnd, 'd MMMM yyyy', { locale: fr })}</Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {[{ color: '#c8e6c9', label: 'Disponible' }, { color: '#1565c0', label: 'En cours' }, { color: '#e3f2fd', label: 'Confirmé' }, { color: '#fff3e0', label: 'En attente' }, { color: '#ffcc80', label: 'Maintenance' }].map((l) => (
              <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: l.color }} /><Typography variant="caption" color="text.secondary">{l.label}</Typography></Box>
            ))}
          </Box>
        </Box>

        {/* Calendar Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, bgcolor: 'white', borderRadius: 3, border: '1px solid #e8ecf1' }}><CircularProgress sx={{ color: '#2c3e50' }} /><Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Chargement du planning...</Typography></Box>
        ) : (
          <Paper elevation={0} sx={{ overflow: 'auto', borderRadius: 3, border: '1px solid #e0e4ea', bgcolor: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <Box sx={{ minWidth: 260 + days.length * dayWidth }}>
              {/* Header Row */}
              <Box sx={{ display: 'flex', borderBottom: '2px solid #e0e4ea', position: 'sticky', top: 0, bgcolor: '#fafbfd', zIndex: 2 }}>
                <Box sx={{ width: 260, minWidth: 260, p: 1.5, borderRight: '1px solid #e0e4ea', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DirectionsCarIcon fontSize="small" sx={{ color: '#2c3e50' }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2c3e50' }}>Véhicule</Typography>
                  <Chip label={`${vehicles.length}`} size="small" sx={{ height: 20, fontSize: '0.7rem', ml: 'auto', bgcolor: '#e8ecf1' }} />
                </Box>
                {days.map((day) => {
                  const todayH = isToday(day);
                  const weekend = isWeekend(day);
                  return (
                    <Box key={day.toISOString()} sx={{ width: dayWidth, minWidth: dayWidth, py: 1, textAlign: 'center', borderRight: '1px solid #e0e4ea', bgcolor: todayH ? 'rgba(44,62,80,0.06)' : weekend ? '#fafbfd' : undefined, position: 'relative' }}>
                      <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 700, color: todayH ? '#2c3e50' : weekend ? '#9e9e9e' : '#78909c', fontSize: viewMode === 'month' ? '0.55rem' : '0.65rem', letterSpacing: 0.5 }}>{format(day, 'EEE', { locale: fr })}</Typography>
                      <Typography variant="body2" fontWeight={todayH ? 800 : 500} sx={{ color: todayH ? '#2c3e50' : 'text.primary', fontSize: viewMode === 'month' ? '0.75rem' : undefined }}>{format(day, 'd')}</Typography>
                      {viewMode !== 'month' && <Typography variant="caption" sx={{ color: '#bdbdbd', fontSize: '0.6rem' }}>{format(day, 'MMM', { locale: fr })}</Typography>}
                      {todayH && <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 20, height: 3, borderRadius: 1.5, bgcolor: '#2c3e50' }} />}
                    </Box>
                  );
                })}
              </Box>

              {/* Vehicle Rows */}
              {vehicles.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}><DirectionsCarIcon sx={{ fontSize: 56, color: '#cfd8dc', mb: 1.5 }} /><Typography variant="h6" fontWeight={600}>Aucun véhicule</Typography></Box>
              ) : vehicles.map((vehicle, idx) => {
                const vehicleReservations = reservationsByVehicle[vehicle.id] || [];
                const mStatus = (vehicle as any).maintenance_status;
                const hasMaintenance = mStatus && mStatus !== 'ready';
                return (
                  <Box key={vehicle.id} sx={{ display: 'flex', borderBottom: '1px solid #eef0f4', minHeight: 56, bgcolor: idx % 2 === 0 ? 'white' : '#fafbfd', transition: 'background-color 0.15s', '&:hover': { bgcolor: '#f0f4f8' } }}>
                    <Box sx={{ width: 260, minWidth: 260, p: 1.5, borderRight: '1px solid #e0e4ea', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={(vehicle as any).primary_image_url} variant="rounded" sx={{ width: 40, height: 40, bgcolor: '#eceff1', fontSize: '0.8rem' }}><DirectionsCarIcon fontSize="small" sx={{ color: '#90a4ae' }} /></Avatar>
                      <Box sx={{ overflow: 'hidden', flex: 1 }}>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ color: '#2c3e50' }}>{vehicle.registration_number}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{vehicle.marque} {vehicle.modele}</Typography>
                      </Box>
                      {hasMaintenance && (
                        <Tooltip title="Alertes maintenance">
                          <IconButton size="small" onClick={(e) => handleMaintenanceClick(e, vehicle)} sx={{ color: mStatus === 'maintenance_needed' ? '#e65100' : '#f9a825' }}>
                            {mStatus === 'maintenance_needed' ? <BuildIcon fontSize="small" /> : <WarningAmberIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Chip label={vehicleReservations.length || '0'} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: vehicleReservations.length > 0 ? '#e3f2fd' : '#e8f5e9', color: vehicleReservations.length > 0 ? '#1565c0' : '#2e7d32' }} />
                    </Box>

                    {days.map((day) => {
                      const dayReservations = vehicleReservations.filter((r) => reservationOverlapsDay(r, day));
                      const todayH = isToday(day);
                      const weekend = isWeekend(day);
                      return (
                        <Box key={day.toISOString()} sx={{ width: dayWidth, minWidth: dayWidth, borderRight: '1px solid #eef0f4', p: viewMode === 'month' ? 0.25 : 0.5, bgcolor: hasMaintenance && dayReservations.length === 0 ? 'rgba(255,152,0,0.05)' : todayH ? 'rgba(44,62,80,0.03)' : weekend ? 'rgba(0,0,0,0.01)' : undefined, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          {dayReservations.length > 0 ? dayReservations.map((res) => (
                            <Tooltip key={res.id} title={`${res.reference || `#${res.id}`} · ${res.customer_first_name || ''} ${res.customer_last_name || ''} · ${STATUS_LABELS[res.status] || res.status}`} arrow placement="top">
                              <Box onClick={() => openDetail(res)} sx={{ borderRadius: 1, bgcolor: STATUS_BG[res.status] || '#e3f2fd', color: STATUS_TEXT[res.status] || '#424242', cursor: 'pointer', overflow: 'hidden', px: viewMode === 'month' ? 0.25 : 1, py: 0.25, mb: 0.25, transition: 'all 0.15s', '&:hover': { transform: 'scale(1.03)', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}>
                                <Typography variant="caption" noWrap sx={{ fontWeight: 700, fontSize: viewMode === 'month' ? '0.5rem' : '0.65rem', display: 'block', lineHeight: 1.3 }}>{res.reference || `#${res.id}`}</Typography>
                                {viewMode !== 'month' && <Typography variant="caption" noWrap sx={{ fontSize: '0.58rem', opacity: 0.85, display: 'block', lineHeight: 1.2 }}>{res.customer_first_name} {res.customer_last_name}</Typography>}
                              </Box>
                            </Tooltip>
                          )) : (
                            <Box sx={{ textAlign: 'center', py: 0.25 }}>
                              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: hasMaintenance ? '#ffcc80' : '#c8e6c9', mx: 'auto' }} />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Summary */}
        {!loading && vehicles.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Paper sx={{ px: 2.5, py: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'white', border: '1px solid #e8ecf1' }}><DirectionsCarIcon sx={{ color: '#2c3e50', fontSize: 20 }} /><Typography variant="body2" fontWeight={600}>{vehicles.length} véhicule{vehicles.length > 1 ? 's' : ''}</Typography></Paper>
            <Paper sx={{ px: 2.5, py: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'white', border: '1px solid #e8ecf1' }}><EventIcon sx={{ color: '#1565c0', fontSize: 20 }} /><Typography variant="body2" fontWeight={600}>{reservations.length} réservation{reservations.length > 1 ? 's' : ''}</Typography></Paper>
          </Box>
        )}
      </Box>

      {/* Maintenance Popover */}
      <Popover open={Boolean(maintenanceAnchor)} anchorEl={maintenanceAnchor} onClose={() => { setMaintenanceAnchor(null); setMaintenanceVehicle(null); }} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#e65100', mb: 1 }}><BuildIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />Alerte maintenance</Typography>
          {maintenanceVehicle && (
            <>
              <Typography variant="body2"><strong>{maintenanceVehicle.registration_number}</strong> — {maintenanceVehicle.marque} {maintenanceVehicle.modele}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Statut: {(maintenanceVehicle as any).maintenance_status === 'maintenance_needed' ? 'Maintenance requise' : 'Maintenance à prévoir'}
              </Typography>
              <Button size="small" variant="outlined" sx={{ mt: 1, borderRadius: 2, textTransform: 'none' }} onClick={() => navigate(`/fleet/${maintenanceVehicle.id}`)}>Voir le véhicule</Button>
            </>
          )}
        </Box>
      </Popover>

      {/* Detail Drawer */}
      <Drawer anchor="right" open={detailDrawerOpen} onClose={() => setDetailDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, borderLeft: 0, boxShadow: '-8px 0 32px rgba(0,0,0,0.08)' } }}>
        <Box sx={{ px: 3, py: 2.5, background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Détail réservation</Typography>
            {detailReservation && <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>{detailReservation.reference || `#${detailReservation.id}`}</Typography>}
          </Box>
          <IconButton onClick={() => setDetailDrawerOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Box>
        {detailReservation && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, overflow: 'auto', flex: 1 }}>
            <Chip label={STATUS_LABELS[detailReservation.status] || detailReservation.status} color={STATUS_COLORS[detailReservation.status] || 'default'} size="medium" sx={{ fontWeight: 700, fontSize: '0.8rem', alignSelf: 'flex-start' }} />
            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#f8f9fa', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #eee' }}><DirectionsCarIcon fontSize="small" sx={{ color: '#2c3e50' }} /><Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2c3e50' }}>Véhicule</Typography></Box>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={(detailReservation as any).vehicle?.primary_image_url} variant="rounded" sx={{ width: 56, height: 56, bgcolor: '#eceff1' }}><DirectionsCarIcon sx={{ color: '#90a4ae' }} /></Avatar>
                <Box><Typography variant="body2" fontWeight={700}>{(detailReservation as any).vehicle?.registration_number}</Typography><Typography variant="body2" color="text.secondary">{(detailReservation as any).vehicle?.marque} {(detailReservation as any).vehicle?.modele}</Typography></Box>
              </Box>
            </Paper>
            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#f8f9fa', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #eee' }}><PersonIcon fontSize="small" sx={{ color: '#2c3e50' }} /><Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2c3e50' }}>Client</Typography></Box>
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" fontWeight={600}>{detailReservation.customer_first_name} {detailReservation.customer_last_name}</Typography>
                {detailReservation.customer_email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}><EmailIcon sx={{ fontSize: 15, color: '#90a4ae' }} /><Typography variant="caption" color="text.secondary">{detailReservation.customer_email}</Typography></Box>}
                {detailReservation.customer_phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}><PhoneIcon sx={{ fontSize: 15, color: '#90a4ae' }} /><Typography variant="caption" color="text.secondary">{detailReservation.customer_phone}</Typography></Box>}
              </Box>
            </Paper>
            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#f8f9fa', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #eee' }}><EventIcon fontSize="small" sx={{ color: '#2c3e50' }} /><Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2c3e50' }}>Dates & Agences</Typography></Box>
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box><Typography variant="caption" color="text.secondary" fontWeight={600}>Départ</Typography><Typography variant="body2" fontWeight={600}>{detailReservation.pickup_datetime ? format(parseISO(detailReservation.pickup_datetime), 'd MMM yyyy HH:mm', { locale: fr }) : '–'}</Typography></Box>
                  <Box><Typography variant="caption" color="text.secondary" fontWeight={600}>Retour</Typography><Typography variant="body2" fontWeight={600}>{detailReservation.return_datetime ? format(parseISO(detailReservation.return_datetime), 'd MMM yyyy HH:mm', { locale: fr }) : '–'}</Typography></Box>
                  <Box><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LocationOnIcon sx={{ fontSize: 14, color: '#90a4ae' }} /><Typography variant="caption" color="text.secondary">Agence départ</Typography></Box><Typography variant="body2">{(detailReservation as any).pickup_agency?.name || (detailReservation as any).pickupAgency?.name || '–'}</Typography></Box>
                  <Box><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LocationOnIcon sx={{ fontSize: 14, color: '#90a4ae' }} /><Typography variant="caption" color="text.secondary">Agence retour</Typography></Box><Typography variant="body2">{(detailReservation as any).return_agency?.name || (detailReservation as any).returnAgency?.name || '–'}</Typography></Box>
                </Box>
              </Box>
            </Paper>
            {detailReservation.total_amount != null && (
              <Paper variant="outlined" sx={{ borderRadius: 2.5, p: 2.5, textAlign: 'center', bgcolor: 'rgba(44,62,80,0.03)', borderColor: 'rgba(44,62,80,0.15)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.5 }}><AttachMoneyIcon sx={{ fontSize: 18, color: '#2c3e50' }} /><Typography variant="caption" color="text.secondary" fontWeight={600}>Montant TTC</Typography></Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#2c3e50' }}>{Number(detailReservation.total_amount).toFixed(2)} €</Typography>
              </Paper>
            )}
            <Button variant="contained" startIcon={<EditIcon />} fullWidth onClick={() => { setDetailDrawerOpen(false); navigate(`/reservations/${detailReservation.id}/edit`); }} sx={{ borderRadius: 2, py: 1.5, mt: 1, textTransform: 'none', fontWeight: 700, bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' }, boxShadow: '0 4px 14px rgba(44,62,80,0.35)' }}>Modifier la réservation</Button>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default Planning;
